import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";
import type { ChangeHandler } from "react-hook-form";

import Button from "@/design-system/button";
import { isClient } from "@/lib/common";
import { sendTelemetryEvent } from "@/lib/telemetry";
import { LoginModalView, languageSelector, useActiveLoginModalContext } from "@/src/contexts";
import LoginResources from "@/src/modals/login-registration/Login.Resources";
import LoginResourcesEn from "@/src/modals/login-registration/Login.Resources.En";
import LoginAndRegistrationForm from "@/src/modals/login-registration/LoginAndRegistrationForm";
import RegistrationResources from "@/src/modals/login-registration/Registration.Resources";
import RegistrationResourcesEn from "@/src/modals/login-registration/Registration.Resources.En";
import SocialLogin from "@/src/modals/login-registration/SocialLogin";
import type { IConsentItem } from "@/src/modals/useConsents";
import { ConsentScreenType, useConsents } from "@/src/modals/useConsents";
import { getFromUrl } from "@Components/Modules/Helpers";
import { useDeviceFingerprint } from "@Components/ZeroFraud";
import * as stylex from "@stylexjs/stylex";

import * as css from "./LoginRegistrationWrapper.module.css";

export interface ILoginRegistrationWrapper {
    onSuccess?: () => void;
    setEmailToMigrate?: (email: string) => void;
}

const LoginRegistrationWrapper: FC<ILoginRegistrationWrapper> = (props) => {
    const { activeLoginModal, setActiveLoginModal, reCaptchaPublicToken } = useActiveLoginModalContext();
    const isRegistration = activeLoginModal === LoginModalView.Registration;
     const fingerprint = useDeviceFingerprint();

    const resources = languageSelector(
        isRegistration ? RegistrationResources : LoginResources,
        isRegistration ? RegistrationResourcesEn : LoginResourcesEn,
    );
    const consentScreenType = isRegistration
        ? ConsentScreenType.RegistrationProcess
        : ConsentScreenType.LoginProcess;
    const consents = useConsents(consentScreenType);

    const [updatedConsents, setUpdatedConsents] = useState<IConsentItem[]>([]);

    const channel = useMemo<"Plugin" | undefined>(
        () => (isClient && getFromUrl("origin")?.toLocaleLowerCase() === "plugin" ? "Plugin" : undefined),
        [],
    );

    useEffect(() => {
        setUpdatedConsents(consents);
    }, [consents]);

    useEffect(() => {
        sendTelemetryEvent({
            actionType: "View",
            eventName: isRegistration ? "common-registerModal" : "common-loginModal",
            channel: channel,
        });
    }, []);

    const onConsentStatusChange: ChangeHandler = (event) => {
        setUpdatedConsents(
            consents.map((value) => {
                const checkbox = event.target as HTMLInputElement;
                if (checkbox.id.includes(value.id)) {
                    value.isSelectedByDefault = checkbox.checked;
                }

                return value;
            }),
        );

        return Promise.resolve();
    };

    return (
        <div className={css.loginRegistrationWrapperComponent}  >
            <LoginAndRegistrationForm
                formTitle={resources.formTitleModal}
                reCaptchaPublicToken={reCaptchaPublicToken}
                resources={resources}
                isRegistration={isRegistration}
                consents={updatedConsents}
                consentScreenType={consentScreenType}
                onConsentStatusChange={onConsentStatusChange}
                forgetPasswordOpen={() => setActiveLoginModal(LoginModalView.ForgetPassword)}
                onSuccess={props.onSuccess}
                setEmailToMigrate={props.setEmailToMigrate}
                channel={channel}
                fingerprint={fingerprint}
            />

            <div className={css.separator}>
                <hr />
                {resources.socialButtonsTitle}
                <hr />
            </div>

            <SocialLogin
                isRegistration={isRegistration}
                consents={updatedConsents}
                consentScreenType={consentScreenType}
                formSubmitText={resources.formButton}
                consentErrorMessage={resources.userConsentError}
                showLessText={resources.showLess}
                showMoreText={resources.showMore}
                modalConfirmationButton={resources.modalConfirmationButton}
                onConsentStatusChange={onConsentStatusChange}
                channel={channel}
            />

            <div className={css.contextSwitcher}>
                <p>{resources.changeScreenButtonText}</p>
                <Button
                    as="button"
                    size="None"
                    variant="transparent"
                    styleX={styles.changeContextBtn}
                    onClick={() =>
                        setActiveLoginModal(
                            isRegistration ? LoginModalView.Login : LoginModalView.Registration,
                        )
                    }
                >
                    {resources.changeScreenButtonLink}
                </Button>
            </div>
        </div>
    );
};

const styles = stylex.create({
    changeContextBtn: {
        margin: 0,
        padding: 0,
        fontSize: "0.875rem",
        fontWeight: "bold",
    },
});

export default LoginRegistrationWrapper;
